import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/InfluencerInstagramAnalyticsPostDetails.graphql';
import { Query, QueryinfluencerInstagramAnalyticsPostDetailsArgs } from '../../__generated__/globalTypes';

export const useInfluencerInstagramAnalyticsPostDetailsQuery = (
  options?: QueryHookOptions<Query, QueryinfluencerInstagramAnalyticsPostDetailsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryinfluencerInstagramAnalyticsPostDetailsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useInfluencerInstagramAnalyticsPostDetailsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryinfluencerInstagramAnalyticsPostDetailsArgs>
): QueryResult<Query, QueryinfluencerInstagramAnalyticsPostDetailsArgs> & {
  getInfluencerInstagramAnalyticsPostDetails: LazyQueryExecFunction<
    Query,
    QueryinfluencerInstagramAnalyticsPostDetailsArgs
  >;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getInfluencerInstagramAnalyticsPostDetails, result] = useLazyQuery<
    Query,
    QueryinfluencerInstagramAnalyticsPostDetailsArgs
  >(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getInfluencerInstagramAnalyticsPostDetails, ...result };
};

export const useInfluencerInstagramAnalyticsPostDetailsPromiseQuery = () => {
  const client = useApolloClient();

  const getInfluencerInstagramAnalyticsPostDetails = useCallback(
    (options?: Omit<QueryOptions<QueryinfluencerInstagramAnalyticsPostDetailsArgs, Query>, 'query'>) =>
      client.query<Query, QueryinfluencerInstagramAnalyticsPostDetailsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getInfluencerInstagramAnalyticsPostDetails };
};
// Suspense Query Hooks
export const useInfluencerInstagramAnalyticsPostDetailsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryinfluencerInstagramAnalyticsPostDetailsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryinfluencerInstagramAnalyticsPostDetailsArgs>(QUERY, {
    ...options
  });

  return result;
};
